import * as React from 'react';
import { cloneDeep, filter, flow, forEach, isEmpty, map, mapValues, get } from 'lodash';
import { IBrandingState, II18nMessages } from '../../../../services/branding/models';
import { Button, Col, Form, HelpBlock, Row } from 'react-bootstrap';
import {
  destroy,
  Field,
  FieldArray,
  formValueSelector,
  getFormSyncErrors,
  InjectedFormProps,
  reduxForm,
  reset,
  submit,
} from 'redux-form';
import { isRequired } from '../../../../util/validators';
import './style.scss';
import TravellersMedical from './components/TravellersMedical';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { IDeclaration, IProduct, ITraveller } from '../../../../data/api/models';
import { IQuoteActions, IQuoteState } from '../../../../services/quote/models';
import FieldFormControl from '../../../../components/FieldFormControl/index';
import * as Commonmark from 'react-commonmark';
import confirm from 'react-confirm2';
import { actions as quoteActions } from '../../../../services/quote/reducer';
import mapMedicalQuestions from '../../../../helpers/medicalDeclaratonQuestions';
import { MedicalInformation } from './components/MedicalInformation';
import { WarningMessages } from '../Verisk/scenes/WarningMessages';
import { IStore } from '../../../../redux/IStore';
import { IVeriskState } from '../../../../services/verisk/models';
import DataLayer from '../../../../helpers/DataLayer';
import { MedicalInformationControlVariant } from './components/MedicalInformationControlVariant';
import { FaUserAlt } from 'react-icons/fa';
import { differenceInDays, parse } from 'date-fns';
import {push} from 'react-router-redux';
import Heap from '../../../../helpers/Heap';
import ButtonBar from '../BackContinueButtonBar';

interface IMedicalDeclaration extends InjectedFormProps {
  i18n: II18nMessages;
  product: IProduct;
  pristine: boolean;
  quote: IQuoteState;
  submitting: boolean;
  onBack: () => void;
  dispatch: any;
  traveller: ITraveller;
  salesChannel: string;
  medicalDeclaration: IDeclaration[];
  travellers: ITraveller[];
  canShowControl?: boolean;

  renderDefinition(name: string, useProvidedString?: string): any;

  actions: {
    submit(name: string);
    quote: IQuoteActions;
    reset(formName: string);
    destroy(formName: string);
    push: (route: string) => void;
  };
  branding: IBrandingState;
  synchronousError: any;
  verisk: IVeriskState;
}

interface IMedicalDeclarationState {
  isScreening: boolean;
  isTerminal: boolean;
  isAnyModalOpen: boolean;
  unableToProvideQuote: boolean;
  controlGroupOverride: boolean | null;
  hasStartedAddingConditions: boolean;
  hasScreenedCondition: boolean;
}

const controlQuestions = {
  medical_questions: [
    {
      id: 1,
      key: 'has-been-prescribed-medication',
      question:
        'In the past 2 years, have you or any named traveller been prescribed medication, received treatment, or seen a medical professional for an illness, injury or medical condition?',
      help_text: '<p class="bold">What we mean by illness, injury or medical condition</p>' +
        '<p class="bold">This includes conditions that:</p>' +
        '<ul><li>conditions that are short term and get better with one-off treatment or medication</li>' +
        '<li>conditions that are long term and need repeat treatment or medication</li>' +
        '<li>have been treated privately, outside the NHS with treatment or medication</li>' +
        '<li>are regularly checked or reviewed each year by a GP, Consultant or medical professional (for example diabetes managed by diet).</li></ul>' +
        '<p class="bold margin-top">Pregnancy</p>' +
        'You do not need to declare Pregnancy as a medical condition. However, if there are pregnancy-related medical conditions or complications which continue to exist after your pregnancy, they should be declared. For more details, take a look at the <a href="https://www.admiral.com/travel-insurance/pregnancy-travel-insurance" target="_blank">Pregnancy Travel Insurance - Admiral</a>.',
    },
    {
      id: 2,
      key: 'has-received-treatment',
      question: `In the past 5 years, have you or any named traveller been diagnosed with or treated for any of the following:
- heart conditions
- lung or respiratory conditions (like asthma, COPD)
- circulation or blood flow problems (like strokes, high blood pressure or cholesterol)
- liver conditions
- cancer
`,
      help_text: '<p class="bold">What we mean by illness, injury or medical condition</p>' +
        '<p class="bold">This includes conditions that:</p>' +
        '<ul><li>conditions that are short term and get better with one-off treatment or medication</li>' +
        '<li>conditions that are long term and need repeat treatment or medication</li>' +
        '<li>have been treated privately, outside the NHS with treatment or medication</li>' +
        '<li>are regularly checked or reviewed each year by a GP, Consultant or medical professional (for example diabetes managed by diet).</li></ul>',
    },
    {
      id: 3,
      key: 'has-psychiatric-condition',
      question: `In the past 5 years, have you or any named traveller been diagnosed with or treated for any psychological or psychiatric conditions such as:
- mental health conditions (like stress, anxiety, depression)
- eating disorders
- drug or alcohol misuse
`,
      help_text: '<p class="bold">What medical conditions need to be declared</p>' +
        '<ul><li>conditions that are short term and resolved with one‑off medication or treatment</li>' +
        '<li>conditions that are long term and managed with repeat medication or treatment</li>' +
        '<li>you have had private treatment or medication for outside the NHS</li>' +
        '<li>are regularly checked or reviewed each year by a GP, Consultant, Psychiatrist or medical professional</li></ul>' +
        '<p class="bold margin-top">Mental Health Conditions:</p>' +
        'If you’d like more information about travel insurance and mental health, or you need extra support while completing your application, you can visit our dedicated page <a href="https://www.admiral.com/magazine/guides/travel/travel-and-mental-health-guide" target="_blank">here</a>.\n' +
        '<p class="bold margin-top">Alcohol and Drug misuse:</p>' +
        'If you’ve had problems with alcohol or drug addiction in the past, please be aware that we don’t cover claims linked to this, or any illness or injury caused by alcohol or drug use.',
    },
    {
      id: 4,
      key: 'is-awaiting-medical-investigation-treatment',
      question:
        'Are you or any named traveller currently waiting for test results, treatment or investigation?',
      help_text: '<p class="bold margin-top">This includes waiting for:</p>' +
        '<ul><li>inpatient or day surgeries</li>' +
        '<li>blood tests, CT scan, MRI scan, X-ray, colonoscopy, biopsy</li>' +
        '<li>resetting or removal of a pacemaker</li>' +
        '<li>consultation with a medical professional or specialist to discuss treatment</li></ul>' +
        '<p class="bold margin-top">Routine tests:</p>' +
        'This doesn’t include the regular checks you have for long‑term health conditions. It only applies if those checks lead to extra tests because something looks unusual, or if you’ve started to have new symptoms.' +
        '<p class="bold margin-top">Pregnancy</p>' +
        'You do not need to declare Pregnancy as a medical condition. However, if there are pregnancy-related medical conditions or complications which continue to exist after your pregnancy, they should be declared. For more details, take a look at the <a href="https://www.admiral.com/travel-insurance/pregnancy-travel-insurance" target="_blank">Pregnancy Travel Insurance - Admiral</a>.',
    },
    {
      id: 5,
      key: 'is-terminal',
      question:
        'Have you or any named traveller been diagnosed with a terminal condition?',
      help_text: 'A terminal illness is a health condition that cannot be cured and is expected to shorten someone’s life. A medical professional gives this diagnosis when the illness is advanced and treatment can’t stop it from progressing.',
    },
  ],
};

const normalizeControlQuestions = (questions) =>
  questions.reduce((acc, q) => {
    acc[q.key] = {
      key: q.key,
      type: 'question',
      question_type: 'boolean',
      content: q.question,
      answer: undefined,
      should_not_screen: q.id === 4,
      help_text: q.help_text,
    };

    return acc;
  }, {});

class MedicalDeclaration extends React.Component<IMedicalDeclaration, IMedicalDeclarationState> {
  public state = {
    isScreening: false,
    isTerminal: false,
    isAnyModalOpen: false,
    unableToProvideQuote: false,
    controlGroupOverride: null,
    hasStartedAddingConditions: false,
    hasScreenedCondition: false,
  };

  private setHasStartedAddingConditions = (value: boolean) => {
    this.setState({ hasStartedAddingConditions: value });
  };

  private setHasScreenedCondition = (value: boolean) => {
    this.setState({ hasScreenedCondition: value });
  };

  private isControlGroup(): boolean {
    if (this.state.controlGroupOverride !== null) {
      return this.state.controlGroupOverride;
    }

    const { quote, travellers } = this.props;
    const tripType = quote?.application?.tripType?.toLowerCase();
    const premiums = quote?.application?.premiums || [];

    if (!tripType) return false;

    const match = tripType === 'single' ? 'single trip' : 'annual';

    const matchingPremiums = premiums.filter(p =>
      p?.schemeType?.toLowerCase().includes(match)
    );

    for (const premium of matchingPremiums) {
      const controlGroup = premium?.information?.controlGroup;

      if (controlGroup !== null && controlGroup !== undefined) {
        const value = Number(controlGroup);
        if (!isNaN(value)) {
          return value >= 0 && value <= 4;
        }
      }
    }

    const dob = travellers?.[0]?.dob;
    if (!dob) return false;

    const diffDays = differenceInDays(
      parse(dob, 'dd/MM/yyyy', new Date()),
      new Date('1901-01-01')
    );

    const lastDigit = Math.abs(Math.round(diffDays * (997 / 1000))) % 10;
    return lastDigit >= 0 && lastDigit <= 4;
  }

  private hasCompletedConditionFlow = () => {
    const { travellers } = this.props;

    return travellers?.some(
      t => t.screening?.meta?.conditions?.length > 0
    );
  };

  public componentWillMount() {
    const { product, change, medicalDeclaration } = this.props;
    if (!isEmpty(medicalDeclaration)) {
      return;
    }

    if (this.isControlGroup()) {
      const normalized = normalizeControlQuestions(controlQuestions.medical_questions);

      change('medicalDeclaration', normalized);
      return;
    }

    change(
      'medicalDeclaration',
      mapValues(
        cloneDeep(product.metadata.medical_declaration_questions),
        (declaration) => ({
          ...declaration,
          answer: undefined,
        }),
      ),
    );
  }

  componentDidMount() {
    DataLayer.handlePageView();

    const { medicalDeclaration, salesChannel, dispatch, change } = this.props;

    if (!this.isControlGroup() && !isEmpty(medicalDeclaration)) {
      const mappedQuestions = mapMedicalQuestions(salesChannel, medicalDeclaration);
      dispatch(change('medicalDeclaration', mappedQuestions));
    }
  }

  private shouldShowScreeningSection = () => {
    const { medicalDeclaration } = this.props;

    if (!medicalDeclaration) {
      return false;
    }

    const questions = filter(medicalDeclaration, (question) => question.type === 'question');
    const unanswered = filter(questions, (question) =>
      question.answer === undefined && question.question_type === 'boolean',
    );
    const trueAnswers = filter(questions, (question) => question.answer === true &&
      !get(question, 'should_not_screen', false));
    return unanswered.length === 0 && trueAnswers.length > 0;
  }

  private setIsScreening = (isScreening: boolean) => {
    this.setState({
      isScreening,
    });
  }

  public goBack = () => {
    const { actions } = this.props;
    Heap.handleBackButtonClick('/medical-declaration');
    actions.push('/travellers-details');
  }

  private renderMedicalQuestion = (item, key) => {
    const { renderDefinition, synchronousError, submitFailed } = this.props;
    const error = get(synchronousError, `medicalDeclaration.${key}.answer`);

    let component = null;

    const yesButton = (
      <Field
        name={`medicalDeclaration[${key}].answer`}
        type="radio"
        format={null}
        value={true}
        complexValue={true}
        className="answer"
        validationMessage={'single'}
        component={FieldFormControl}
        validate={[isRequired]}
      >
        <div className="medical-btn yes">Yes</div>
      </Field>
    );

    const noButton = (
      <Field
        name={`medicalDeclaration[${key}].answer`}
        type="radio"
        format={null}
        value={false}
        complexValue={true}
        className="answer"
        validationMessage={'single'}
        component={FieldFormControl}
        validate={[isRequired]}
      >
        <div className="medical-btn no">No</div>
      </Field>
    );

    const errorBlock = (
      error && submitFailed && (
        <Row style={{textAlign: 'center'}}>
          <Col>
            <div className={error ? 'has-error' : ''}>
              <HelpBlock>{error}</HelpBlock>
            </div>
          </Col>
        </Row>
      )
    );

    if (item.question_type === 'boolean') {
      component = (
        <>
          {this.isControlGroup() ? (
            <div className="answer answer-control">
              {yesButton}
              {noButton}
            </div>
          ) : (
            <>
              <Col xs={6}>{yesButton}</Col>
              <Col xs={6}>{noButton}</Col>
            </>
          )}

          {errorBlock}
        </>
      );
    }

    if (item.question_type === 'number') {
      component = (
        <div>
          <Field
            name={`medicalDeclaration[${key}].answer`}
            className="answer-number"
            component={FieldFormControl}
          />
        </div>
      );
    }

    return (
      <div className="medical-question medical-section" key={key}>
        <Row>
          <Col sm={this.isControlGroup() ? 12 : 7}>
            <div className="question-inline">
              <Commonmark source={item.content}/>
              <div className="remove-margins-from-children">
                {renderDefinition(item.true_key, item.help_text)}
              </div>
            </div>
          </Col>
          <Col sm={this.isControlGroup() ? 12 : 5}>
            {component}
          </Col>
        </Row>
      </div>
    );
  }

  private renderMedicalHeader = (item, key) => {
    return (
      <div key={key}>
        <p> v
          {item.content}
        </p>
      </div>
    );
  }

  public renderQuestions() {
    const { medicalDeclaration } = this.props;

    const displayKeys = [];

    forEach(medicalDeclaration, (declaration: IDeclaration, key) => {
      const answer = declaration.answer;
      const questionType = declaration.question_type;

      if (questionType === 'boolean' && answer === 'true') {
        if (declaration.next_key) {
          displayKeys.push(declaration.next_key);
        }

        if (declaration.true_key) {
          displayKeys.push(declaration.true_key);
        }
      }

      if (questionType === 'boolean' && answer === 'false') {
        if (declaration.next_key) {
          displayKeys.push(declaration.next_key);
        }

        if (declaration.false_key) {
          displayKeys.push(declaration.false_key);
        }
      }
    });

    return (
      <div className="medical-questions-container">
        {map(medicalDeclaration, (question: any, key) => {
          let component;

          if (question.display === false && displayKeys.indexOf(key) === -1) {
            return null;
          }

          if (question.type === 'header') {
            component = this.renderMedicalHeader(question, key);
          }

          if (question.type === 'question') {
            component = this.renderMedicalQuestion(question, key);
          }

          return component;
        })}
      </div>
    );
  }

  private checkIfConditionWasDeclared = () => {
    const { travellers, medicalDeclaration } = this.props;

    const questions = filter(medicalDeclaration, (question) => question.type === 'question');
    const unanswered = filter(questions, (question) =>
      question.answer === undefined && question.question_type === 'boolean',
    );
    const trueAnswers = filter(questions, (question) => question.answer === true &&
      !get(question, 'should_not_screen', false));
    const travellerScreening = filter(travellers, (traveller) => traveller.screening);
    const travellersLength = travellers.length;
    const shouldScreen = unanswered.length === 0 && trueAnswers.length > 0;

    if (!shouldScreen) {
      this.checkForErrorsBeforeSubmit();
    }

    if (shouldScreen && travellerScreening.length === travellersLength) {
      this.checkForErrorsBeforeSubmit();
    }

    if (shouldScreen && travellerScreening.length < travellersLength && travellerScreening.length === 0) {
      this.setState({isAnyModalOpen: true});
      confirm('Finish Medical Screening', {
        description: (
          <span>
            <h4>Warning - You cannot continue without declaring a medical condition.</h4>
            Please click on 'Add Conditions' button to add your conditions.
            <br/>
          </span>
        ),
        done: () => {
          this.setState({isAnyModalOpen: false});
          return;
        },
        close: () => {
          this.setState({isAnyModalOpen: false});
          return;
        },
      });
    }

    if (shouldScreen && travellerScreening.length < travellersLength && travellerScreening.length > 0) {
      this.checkForErrorsBeforeSubmit();
    }
  }

  private checkForErrorsBeforeSubmit = () => {
    const { actions, synchronousError } = this.props;
    Promise.resolve(actions.submit('quote')).then(() => {
      if (synchronousError && !isEmpty(synchronousError)) {
        window.scrollTo(0, 0);
      }
    });
  }

  private isRenewal() {
    return this.props.quote.application.quoteType === 'renewal';
  }

  public render() {
    const {
      i18n,
      product,
      handleSubmit,
      medicalDeclaration,
      submitting,
      valid,
      change,
      submitFailed,
      branding,
      verisk,
      travellers,
    } = this.props;
    const { metadata } = product;
    const { isAnyModalOpen, unableToProvideQuote } = this.state;
    if (!medicalDeclaration) {
      return false;
    }
    const shouldHideContinueButton =
      this.shouldShowScreeningSection() &&
      !this.hasCompletedConditionFlow() &&
      this.isControlGroup();

    return (
      <Form onSubmit={handleSubmit} autoComplete="off">
        {isRequired && submitFailed && !valid && (
          <div className="error-block-container" id="error-container">
            <h1>Whoops!</h1>
            <p>Missing something? Please check the areas marked in orange before you continue.</p>
          </div>
        )}
        {!unableToProvideQuote ? (
          <div className={this.isControlGroup() ? 'steps-container control-group' : 'steps-container'}>
            {!this.isControlGroup() && (
              <div className="section-title">
                <h2>Medical Declaration</h2>
              </div>
            )}

            {this.isControlGroup() ? <MedicalInformationControlVariant/> : <MedicalInformation/>}

            {this.isControlGroup() && (
              <div className="traveller-information">
                <div className="traveller-names">
                  <h2>Please share medical information for the named travellers:</h2>

                  {travellers && travellers.length > 0 && travellers.map((traveller, index) => {
                    const firstName = traveller.firstName || '';
                    const lastName = traveller.lastName || '';

                    return (
                      <p key={index}>
                        <FaUserAlt/> {firstName} {lastName}
                      </p>
                    );
                  })}
                </div>
                <span>If you’re not sure about a medical condition or dates, your GP surgery may be able to clarify this for you.</span>
              </div>
            )}
            <div className="medical-section-group">

              {this.renderQuestions()}

              <div className="medical-condition-container" hidden={!this.shouldShowScreeningSection()}>
                <div className="section-title">
                  <h2>Add Medical Condition(s)</h2>
                </div>

                {(this.isRenewal() && branding.channel.channelType !== 'AGG') && (
                  <div className="section">
                    <Commonmark className="important"
                                source={product.metadata.definitions.direct_renewal_medical_conditions}/>
                  </div>
                )}

                {this.isControlGroup() && (() => {
                  let message = 'Next step - please add the medical condition(s).';

                  if (this.hasCompletedConditionFlow() && !(this.state.hasStartedAddingConditions || this.state.hasScreenedCondition)) {
                    message = 'Once you\'ve shared all medical conditions please continue.';
                  }
                  if (this.state.hasStartedAddingConditions) {
                    message = 'You are adding conditions for:';
                  }
                  if (this.state.hasScreenedCondition) {
                    message = 'Please answer the below questions about your condition.';
                  }

                  return (
                    <p className="med-cond-p">
                      {message}
                    </p>
                  );
                })()}

                <FieldArray
                  name="travellers"
                  i18n={i18n}
                  change={change}
                  component={TravellersMedical}
                  questions={metadata.medical_declaration_questions}
                  setIsScreening={this.setIsScreening}
                  rerenderOnEveryChange={true}
                  isControlGroup={this.isControlGroup()}
                  setHasStartedAddingConditions={this.setHasStartedAddingConditions}
                  setHasScreenedCondition={this.setHasScreenedCondition}
                />
              </div>

              <WarningMessages warningMessage={verisk?.errors}/>

              <Row>
                <Col>
                  <ButtonBar
                    barClassName="pr-15 pl-15"
                    hidden={this.state.isScreening === true || shouldHideContinueButton}
                    continueDisabled={submitting || (submitFailed && !valid) || isAnyModalOpen}
                    backDisabled={submitting}
                    onContinue={this.checkIfConditionWasDeclared}
                    onBack={this.goBack}
                  />
                </Col>
              </Row>

              <div className="toc section-important" style={{marginTop: '25px'}}
                   hidden={this.state.isScreening === true}>
                By clicking continue you agree to our <a
                href="https://www.admiral.com/insurance/your-policy/conditions-of-use.php?&contentOnly=1&_ga=2.7793211.828839540.1508235797-2125578306.1459951786&_gac=1.217769634.1505922935.EAIaIQobChMI_rW5lpC01gIVzrztCh1dwA5aEAAYASAAEgJX9PD_BwE"
                target="_blank">conditions of use </a>
                and <a
                href="https://www.admiral.com/your-privacy-and-security/?&contentOnly=1&_ga=2.7793211.828839540.1508235797-2125578306.1459951786&_gac=1.217769634.1505922935.EAIaIQobChMI_rW5lpC01gIVzrztCh1dwA5aEAAYASAAEgJX9PD_BwE"
                target="_blank">privacy statement</a>
              </div>

            </div>
          </div>
        ) : (
          <div className="steps-container unable-box">
            <h1>Sorry, we are unable to provide a quote</h1>
            <h4>
              We are unable to provide any cover if you or anyone on your policy have been placed
              on a waiting list for treatment or investigation, or been given a terminal prognosis.
            </h4>
          </div>
        )}
      </Form>
    );
  }
}

let selector;

export default flow([
  reduxForm({
    form: 'quote',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const medicalDeclaration = selector(state, 'medicalDeclaration');
      const travellers = selector(state, 'travellers');
      const steps = selector(state, 'steps');
      const salesChannel = selector(state, 'salesChannel');
      const synchronousError = getFormSyncErrors('quote')(state);
      const verisk = state.verisk;

      return {
        medicalDeclaration,
        travellers,
        steps,
        synchronousError,
        salesChannel,
        verisk
      };
    },
    (dispatch) => ({
      dispatch,
      actions: {
        submit: bindActionCreators(submit, dispatch),
        quote: bindActionCreators({...quoteActions}, dispatch),
        reset: bindActionCreators(reset, dispatch),
        destroy: bindActionCreators(destroy, dispatch),
        push: bindActionCreators(push, dispatch),
      },
    }),
  ),
])(MedicalDeclaration);
